import type * as THREE from 'three';
import { type IObjectViewModel, SceneItemAction, SceneItemKind } from '@/engine/app/interfaces';
import type { IIndicationStrategy } from '../Indications.controller';
import type { IndicationsService } from '../Indications.service';
import { Tools } from '../ToolStateStore';
import type { SplineIndicationModel } from './SplineIndication.model';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { IIndicationAdder, IIndicationsViewModelFactory } from '../interfaces';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { ICommandProcessor } from '../../core/interfaces/Command';
import type { IRemover } from '@/engine/UIPresentation/Interfaces';

export class AreaIndicationStrategy implements IIndicationStrategy {
  private readonly _baseIndicationLabel = 'Area';

  private _indicationsService: IndicationsService;

  private _model?: SplineIndicationModel;

  private _indicationViewModel?: IObjectViewModel;

  private _planningAlgorithm: PlanningAlgorithm;

  private _vmFactory: IIndicationsViewModelFactory;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _indicatedPointThreshold = 0.8;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _remover: IRemover;

  private readonly _adder: IIndicationAdder<DT.Polyline>;

  public constructor(
    indicationsService: IndicationsService,
    planningAlgorithm: PlanningAlgorithm,
    commandProcessor: ICommandProcessor,
    adder: IIndicationAdder<DT.Polyline>,
    remover: IRemover
  ) {
    this._indicationsService = indicationsService;
    this._commandProcessor = commandProcessor;
    this._planningAlgorithm = planningAlgorithm;
    this._vmFactory = this._indicationsService.vmFactory;
    this._loggerService = this._indicationsService.loggerService;
    this._adder = adder;
    this._remover = remover;
  }

  public async onPointAddAsync(point: THREE.Vector3): Promise<void> {
    if (!this._model) {
      [this._model, this._indicationViewModel] = await this._initializeIndication(point);
    }
    if (this._model.basePts.length > 3 && this._model.basePts[0].distanceTo(point) < this._indicatedPointThreshold) {
      await this.finalizeIndicationAsync();
    } else {
      this._model.addPoint(point);
    }
  }

  public onPointMove(point: THREE.Vector3): void {
    if (!this._model) {
      return;
    }
    const isOverFirstPoint =
      this._model.basePts.length > 3 && this._model.basePts[0].distanceTo(point) < this._indicatedPointThreshold;
    this._indicationViewModel?.setHighlighting(isOverFirstPoint);
    this._model.updateLastPoint(point);
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async dispose(): Promise<void> {
    return this._indicationsService.rollbackIndicationAsync();
  }

  private async _initializeIndication(
    point: THREE.Vector3
  ): Promise<[model: SplineIndicationModel, viewModel: IObjectViewModel]> {
    const { model, viewModel } = (await this._indicationsService.initializeIndicationAsync(Tools.Area)) as unknown as {
      model: SplineIndicationModel;
      viewModel: IObjectViewModel;
    };
    model.addPoint(point);
    return [model, viewModel];
  }

  public async finalizeIndicationAsync(): Promise<void> {
    if (!this._model || this._model.basePts.length < 4 || !this._indicationViewModel) {
      return;
    }
    await this._indicationsService.finalizeIndicationAsync();
    await this._indicationsService.removeIndicationAsync(this._indicationViewModel);

    const splineDescriptor = this._indicationViewModel.descriptor;
    const areaDescriptor = splineDescriptor + '.area';
    const points = this._model.basePts.slice(0, -1);
    const phaseId = this._indicationViewModel.phaseId;

    const splineVM = this._vmFactory.createViewModel(SceneItemKind.IndicatedSpline, phaseId, splineDescriptor, [
      areaDescriptor
    ]);

    const areaVM = this._vmFactory.createMeasurementViewModel(
      SceneItemKind.IndicatedAreaMeasurement,
      phaseId,
      areaDescriptor,
      [splineDescriptor],
      this._indicationsService.getIndicationLabel(this._baseIndicationLabel, SceneItemKind.IndicatedAreaMeasurement)
    );

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.executeAnonymousCommand(
      (): Promise<undefined> => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
        this._adder.addIndication(
          new DT.Polyline({
            points: points,
            isClosed: true
          }),
          areaVM,
          splineVM
        );
        return Promise.resolve(undefined);
      },
      async (): Promise<undefined> => {
        this._remover.removeByDescriptor(areaDescriptor, [splineDescriptor]);
        return Promise.resolve(undefined);
      }
    );

    this._loggerService.dispatchSceneItemAction(SceneItemAction.IndicationAdded, areaVM);

    this._model = undefined;
    this._indicationViewModel = undefined;
  }
}
